import { Colors } from '@quizzach/styles';
import { QuizSlidesContainer } from '@quizzach/play-styles';
import {
	ApiFailedReason,
	QuizOutletContextType,
	QuestionType,
} from '@quizzach/interfaces';
import {
	AbcdQuestionAnswerDesktop,
	AssociationQuestionAnswerDesktop,
	ConnectionQuestionAnswerDesktop,
	StandardQuestionAnswerDesktop,
	StandardQuestionAnswerMobile,
	AbcdQuestionAnswerMobile,
	AssociationQuestionAnswerMobile,
	ConnectionQuestionAnswerMobile,
	VisualQuestionAnswerDesktop,
	VisualQuestionAnswerMobile,
	EstimationQuestionAnswerMobile,
	EstimationQuestionAnswerDesktop,
	BottomNavMobile,
} from '@quizzach/layouts';
import { scrollToTop } from '@quizzach/utils';
import { useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { AlertMessage, BottomNav, FitToViewport } from '@quizzach/components';
import { useWindowListener } from '@quizzach/contexts';

// Answer component map for mobile and desktop variants
const ANSWER_COMPONENTS = {
	[QuestionType.standard]: {
		Mobile: StandardQuestionAnswerMobile,
		Desktop: StandardQuestionAnswerDesktop,
	},
	[QuestionType.abcd]: {
		Mobile: AbcdQuestionAnswerMobile,
		Desktop: AbcdQuestionAnswerDesktop,
	},
	[QuestionType.association]: {
		Mobile: AssociationQuestionAnswerMobile,
		Desktop: AssociationQuestionAnswerDesktop,
	},
	[QuestionType.connection]: {
		Mobile: ConnectionQuestionAnswerMobile,
		Desktop: ConnectionQuestionAnswerDesktop,
	},
	[QuestionType.visual]: {
		Mobile: VisualQuestionAnswerMobile,
		Desktop: VisualQuestionAnswerDesktop,
	},
	[QuestionType.estimation]: {
		Mobile: EstimationQuestionAnswerMobile,
		Desktop: EstimationQuestionAnswerDesktop,
	},
} as const;

export const AnswerSlides = () => {
	const {
		quiz,
		currentQuestionNumber,
		mode,
		error,
		quizType,
		handleNext,
		setFeedback,
		handlePrevious,
	} = useOutletContext<QuizOutletContextType>();

	const { touchDevice } = useWindowListener();

	// Extract current question to avoid repeated array access
	const currentQuestion = quiz?.questions[currentQuestionNumber - 1];
	const currentQuestionType = currentQuestion?.type;

	useEffect(() => {
		scrollToTop();
	}, [currentQuestionNumber]);

	if (!quiz) {
		return <></>;
	}

	const isPremadeOrEstimation =
		quizType === 'premade' || currentQuestionType === QuestionType.estimation;

	// Helper function to render the appropriate answer component
	const renderAnswerComponent = () => {
		if (!currentQuestionType || !ANSWER_COMPONENTS[currentQuestionType]) {
			return null;
		}

		const componentVariant = touchDevice ? 'Mobile' : 'Desktop';
		const AnswerComponent = ANSWER_COMPONENTS[currentQuestionType][
			componentVariant
		] as any;

		// Common props for all answer components
		const commonProps = {
			questionContainerColor: Colors.none,
			answerContainerColor: Colors.answerContainer,
			currentQuestion,
		};

		// Desktop estimation doesn't have currentQuestionNumber
		if (!touchDevice && currentQuestionType === QuestionType.estimation) {
			return <AnswerComponent {...commonProps} />;
		}

		// Question types that support hideQuestion prop (mobile only)
		const supportsHideQuestion =
			currentQuestionType === QuestionType.standard ||
			currentQuestionType === QuestionType.abcd ||
			currentQuestionType === QuestionType.connection ||
			currentQuestionType === QuestionType.visual ||
			currentQuestionType === QuestionType.estimation;

		// Device-specific props: mobile includes totalQuestions and hideQuestion on qaqa mode
		const deviceSpecificProps = touchDevice
			? {
					currentQuestionNumber,
					totalQuestions: quiz?.questions?.length,
					...(supportsHideQuestion && { hideQuestion: mode === 'qaqa' }),
				}
			: {
					currentQuestionNumber,
				};

		return <AnswerComponent {...commonProps} {...deviceSpecificProps} />;
	};

	return (
		<>
			{error && <AlertMessage code={ApiFailedReason.default} type='error' />}

			{!error && touchDevice && (
				<QuizSlidesContainer data-id='quiz-slides-container-04-mobile'>
					{/* Mobile scrolls naturally; the fit-to-viewport scaling is a
					    desktop/presentation concern. */}
					<div>{renderAnswerComponent()}</div>
					<BottomNavMobile
						question={currentQuestion}
						handleNext={handleNext}
						handlePrevious={handlePrevious}
						setFeedback={setFeedback}
						isPremadeOrEstimation={isPremadeOrEstimation}
						type='feedback'
					/>
				</QuizSlidesContainer>
			)}
			{!error && !touchDevice && (
				<QuizSlidesContainer data-id='quiz-slides-container-04-desktop'>
					<FitToViewport>{renderAnswerComponent()}</FitToViewport>
					<BottomNav
						question={currentQuestion}
						handleNext={handleNext}
						handlePrevious={handlePrevious}
						setFeedback={setFeedback}
						isPremadeOrEstimation={isPremadeOrEstimation}
						type='feedback'
					/>
				</QuizSlidesContainer>
			)}
		</>
	);
};
